/** Widget library, layer list (select / visibility / z-order / remove) and starter templates. */
import { useTranslation } from "react-i18next";
import type {
  BroadcastLayout,
  BroadcastWidgetType,
} from "../../lib/broadcastApi";
import {
  createWidget,
  MAX_WIDGETS,
  reorderZ,
  WIDGET_TYPES,
} from "./editorMath";
import { applyTemplate, TEMPLATE_IDS, type TemplateId } from "./templates";

interface Props {
  layout: BroadcastLayout;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  update: (mutate: (draft: BroadcastLayout) => void) => void;
}

const btn =
  "rounded-md border border-slate-500 px-2 py-1 text-xs hover:bg-slate-800 disabled:opacity-40";

export default function WidgetsPanel({
  layout,
  selectedId,
  onSelect,
  update,
}: Props) {
  const { t } = useTranslation("broadcast");
  const full = layout.widgets.length >= MAX_WIDGETS;
  const layers = [...layout.widgets].sort((a, b) => b.z - a.z);

  const add = (type: BroadcastWidgetType) => {
    const created = createWidget(type, layout.widgets, layout.stage.aspect);
    if (!created) return;
    update((d) => {
      d.widgets.push(created);
    });
    onSelect(created.id);
  };

  return (
    <div
      className="rounded-lg border border-slate-700 p-4 space-y-4"
      data-testid="control-widgets-panel"
    >
      <h2 className="text-lg font-semibold">{t("control.widgets")}</h2>

      <div>
        <h3 className="mb-2 text-sm font-medium text-slate-200">
          {t("control.templates")}
        </h3>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_IDS.map((id: TemplateId) => (
            <button
              key={id}
              type="button"
              data-testid={`control-template-${id}`}
              className={btn}
              onClick={() => {
                update((d) => applyTemplate(d, id));
                onSelect(null);
              }}
            >
              {t(`control.template.${id}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-slate-200">
          {t("control.library")}
        </h3>
        <div className="flex flex-wrap gap-2">
          {WIDGET_TYPES.filter((type) => type !== "image").map((type) => (
            <button
              key={type}
              type="button"
              disabled={full}
              data-testid={`control-add-${type}`}
              className={btn}
              onClick={() => add(type)}
            >
              + {t(`control.widgetTypes.${type}`)}
            </button>
          ))}
        </div>
        {full && (
          <p className="mt-1 text-xs text-amber-300">
            {t("control.maxWidgets")}
          </p>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-slate-200">
          {t("control.layers")}
        </h3>
        <ul className="space-y-1" data-testid="control-layers">
          {layers.map((w) => (
            <li
              key={w.id}
              data-testid={`control-layer-${w.id}`}
              data-selected={w.id === selectedId ? "true" : "false"}
              className={`flex items-center gap-2 rounded-md border px-2 py-1 text-sm ${
                w.id === selectedId
                  ? "border-sky-400 bg-slate-800"
                  : "border-slate-700"
              }`}
            >
              <button
                type="button"
                className="flex-1 truncate text-left"
                data-testid={`control-layer-select-${w.id}`}
                onClick={() => onSelect(w.id)}
              >
                {t(`control.widgetTypes.${w.type}`)}{" "}
                <span className="text-xs text-slate-400">{w.id}</span>
              </button>
              <button
                type="button"
                className={btn}
                aria-label={t("control.bringForward")}
                data-testid={`control-layer-up-${w.id}`}
                onClick={() =>
                  update((d) => {
                    d.widgets = reorderZ(d.widgets, w.id, "up");
                  })
                }
              >
                ▲
              </button>
              <button
                type="button"
                className={btn}
                aria-label={t("control.sendBackward")}
                data-testid={`control-layer-down-${w.id}`}
                onClick={() =>
                  update((d) => {
                    d.widgets = reorderZ(d.widgets, w.id, "down");
                  })
                }
              >
                ▼
              </button>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={w.visible}
                  data-testid={`control-layer-visible-${w.id}`}
                  onChange={(e) =>
                    update((d) => {
                      const target = d.widgets.find((x) => x.id === w.id);
                      if (target) target.visible = e.target.checked;
                    })
                  }
                />
                {t("control.visible")}
              </label>
              <button
                type="button"
                className={`${btn} text-red-300`}
                aria-label={t("control.removeWidget")}
                data-testid={`control-layer-remove-${w.id}`}
                onClick={() => {
                  update((d) => {
                    d.widgets = d.widgets.filter((x) => x.id !== w.id);
                  });
                  if (selectedId === w.id) onSelect(null);
                }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
